import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { AppContext } from '@r-app/app-context';
import { useSubscription } from '@rta/rta-blueprint-library';
import { renderTracking } from '@r-services/render-tracking/render-tracking-service';

import { RenderSkuAnnotation } from './render-sku-annotation';
import { useSkuAnnotation } from './sku-annotation-context';
import type { SkuAnnotationTool } from './sku-annotation-toolbar';

export type RenderSkuAnnotationsProps = {
	activeTool: SkuAnnotationTool;
	selectedAnnotationId: string | null;
	onSelectAnnotation: (id: string | null) => void;
	onEditAnnotation?: (id: string) => void;
};

export const RenderSkuAnnotations = (props: RenderSkuAnnotationsProps): React.ReactElement => {
	const { activeTool, selectedAnnotationId, onSelectAnnotation, onEditAnnotation } = props;
	const context = useContext(AppContext);
	const controller = context?.controller;
	const kitchen = controller?.kitchen;
	const options = controller?.options;
	const [version, setVersion] = useState(0);
	const { selectedAnnotationIds, editingAnnotationId, setEditingAnnotationId, contextMenu, setContextMenu, pendingSaveRef } = useSkuAnnotation();

	// Subscribe to font size changes to re-render when fontSizeFactor is updated
	useSubscription(controller, false, [['designer-options-adjust-font-size', 'updated']]);

	const fontSizeFactor = options?.dimensionFontSizeFactor ?? 1;

	useEffect(() => {
		if (!kitchen) return;
		const subs: Array<string> = [];
		subs.push(kitchen.subscribe('sku_annotations', '*', () => setVersion((v) => v + 1)));
		subs.push(kitchen.subscribe('sku_annotation', '*', () => setVersion((v) => v + 1)));
		return () => { subs.forEach((id) => { kitchen.unsubscribe(id); }); };
	}, [kitchen]);

	// Delete/Backspace: deletes every selected id (shared selectedAnnotationIds, same pattern blueprint-page-annotation-overlay.tsx uses) so this stays correct if SKU overhead ever wires up multi-select
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (selectedAnnotationIds.length === 0 || editingAnnotationId) return;
			if (e.key === 'Delete' || e.key === 'Backspace') {
				e.preventDefault();
				selectedAnnotationIds.forEach((id) => kitchen?.skuAnnotations.delete(id));
				onSelectAnnotation(null);
				setEditingAnnotationId(null);
				setContextMenu(null);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => { document.removeEventListener('keydown', handleKeyDown); };
	}, [selectedAnnotationIds, editingAnnotationId, kitchen, onSelectAnnotation, setEditingAnnotationId, setContextMenu]);

	// Click-outside deselection: clicking anywhere outside an annotation deselects it
	useEffect(() => {
		if (!selectedAnnotationId) return;

		const handleMouseDown = (e: MouseEvent): void => {
			const target = e.target as Element;
			if (!target) return;

			// Check if click is inside an annotation element or the toolbar/context menu
			const insideAnnotation = target.closest('[type="sku-annotations-layer"]') !== null;
			const insideToolbar = target.closest('[data-sku-toolbar]') !== null;
			const insideContextMenu = target.closest('[data-annotation-context-menu]') !== null;

			if (!insideAnnotation && !insideToolbar && !insideContextMenu) {
				// Save any pending edit text before clearing editing state,
				// since unmounting the textarea prevents onBlur from firing
				if (pendingSaveRef.current) {
					pendingSaveRef.current();
					pendingSaveRef.current = null;
				}
				onSelectAnnotation(null);
				setEditingAnnotationId(null);
				setContextMenu(null);
			}
		};

		// Use setTimeout to avoid catching the same click that selected the annotation
		const timer = setTimeout(() => {
			document.addEventListener('mousedown', handleMouseDown);
		}, 0);
		return () => {
			clearTimeout(timer);
			document.removeEventListener('mousedown', handleMouseDown);
		};
	}, [selectedAnnotationId, onSelectAnnotation, setEditingAnnotationId, setContextMenu]);

	// Close context menu on any click or scroll
	useEffect(() => {
		if (!contextMenu) return;
		const close = (): void => { setContextMenu(null); };
		document.addEventListener('mousedown', close);
		document.addEventListener('scroll', close, true);
		return () => {
			document.removeEventListener('mousedown', close);
			document.removeEventListener('scroll', close, true);
		};
	}, [contextMenu, setContextMenu]);

	const handleCloseEdit = useCallback((): void => { setEditingAnnotationId(null); }, [setEditingAnnotationId]);

	const handleContextMenu = useCallback((e: React.MouseEvent, annotationId: string): void => {
		onSelectAnnotation(annotationId);
		setContextMenu({ x: e.clientX, y: e.clientY, annotationId });
	}, [onSelectAnnotation, setContextMenu]);

	const handleContextMenuDelete = useCallback((): void => {
		if (!contextMenu || !kitchen) return;
		kitchen.skuAnnotations.delete(contextMenu.annotationId);
		onSelectAnnotation(null);
		setEditingAnnotationId(null);
		setContextMenu(null);
	}, [contextMenu, kitchen, onSelectAnnotation, setEditingAnnotationId, setContextMenu]);

	renderTracking.incrementRender('RenderSkuAnnotations');

	if (!kitchen) return <></>;

	const annotations = kitchen.skuAnnotations?.children ?? [];

	return (
		<>
			<g type="sku-annotations-layer">
				{annotations.map((annotation) => (
					<RenderSkuAnnotation
						key={annotation.id}
						annotation={annotation}
						activeTool={activeTool}
						isSelected={annotation.id === selectedAnnotationId}
						isEditing={annotation.id === editingAnnotationId}
						fontSizeFactor={fontSizeFactor}
						onSelectAnnotation={onSelectAnnotation}
						onEditAnnotation={onEditAnnotation}
						onCloseEdit={handleCloseEdit}
						onContextMenu={handleContextMenu}
					/>
				))}
			</g>

			{/* Annotation right-click context menu rendered via portal */}
			{contextMenu && createPortal(
				<div
					data-annotation-context-menu="true"
					style={{
						position: 'fixed',
						top: contextMenu.y,
						left: contextMenu.x,
						zIndex: 10000,
						background: 'white',
						border: '1px solid #ccc',
						borderRadius: '6px',
						boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
						padding: '4px 0',
						minWidth: '140px',
						fontSize: '13px'
					}}
					onMouseDown={(e) => e.stopPropagation()}
				>
					<button
						onClick={handleContextMenuDelete}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							width: '100%',
							padding: '8px 16px',
							border: 'none',
							background: 'transparent',
							cursor: 'pointer',
							fontSize: '13px',
							color: '#e53e3e',
							textAlign: 'left'
						}}
						onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = '#f7f7f7'; }}
						onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = 'transparent'; }}
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<polyline points="3 6 5 6 21 6" />
							<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
						</svg>
						Delete
					</button>
				</div>,
				document.body
			)}
		</>
	);
};
